import { Component } from 'react';

import { graphql } from '@apollo/client/react/hoc';
import { stringify } from '@stratumn/canonicaljson';
import to from 'await-to-js';
import gql from 'graphql-tag';
import compose from 'lodash.flowright';
import { Archive, Database, GitCompare, Settings, Wrench } from 'lucide-react';
import Path from 'path-to-regexp';
import PropTypes from 'prop-types';
import { Route, Switch, withRouter } from 'react-router-dom';

import { Button } from '@/shadcn/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';

import { withError } from '@/components/errorBoundary/errorBoundary';
import { Footer, Navbar } from '@/components/layouts';
import { notify } from '@/components/toast';
import TraceIconSpinner from '@/components/ui/traceIconSpinner';
import { JsonEditor } from '@/components/ui/utils/jsonEditor/jsonEditor';
import {
  getUserInfoDisplayConfig,
  manageLocalStorage,
  sectionsLocalStorage
} from '@/components/ui/utils/localStorage';
import { Widget } from '@/components/ui/widget';
import { TOOLTIP_PORTAL, TRAY_PORTAL_RIGHT } from '@/constant/htmlIds';
import { TRACE_LINKS_FIRST } from '@/constant/pagination';
import {
  ROUTE_INSPECT_TRACE_LINK,
  ROUTE_NEW_LINK,
  ROUTE_WORKFLOW_DASHBOARD,
  ROUTE_WORKFLOW_OVERVIEW
} from '@/constant/routes';
import { LocalStorageContext, withUser } from '@/contexts';
import { FILTERS_QUERY } from '@/gql/localQueries';
import {
  TYPE_UPDATE_WORKFLOW_CONFIG_PAYLOAD,
  TYPE_WORKFLOW,
  TYPE_WORKFLOW_CONFIG
} from '@/gql/types';
import { getNextActionsArray } from '@/utils';
import { hasElevatedRights } from '@/utils/privileges';
import { buildWorkflowContext, WorkflowContext } from '@/utils/workflowContext';
import { ActionsList } from '../ui/actionsList/actionList';
import Vaul from '../ui/vaul/vaul';
import { CompareLinksDiffModal } from './compareLinksDiffModal/compareLinksDiffModal';
import fragments from './fragments';
import { useTraceLinkAnchor } from './hooks/useTraceLinkAnchor';
import { useTraceLinksPagination } from './hooks/useTraceLinksPagination';
import { SegmentInfo } from './segmentInfo/segmentInfo';
import { SegmentList } from './segmentList/segmentList';
import { TraceInspectorContext } from './traceInspector.context';
import { isMigrationLink } from './traceInspector.utils';

export class TraceInspectorImpl extends Component {
  static propTypes = {
    traceQuery: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    updateTraceInfoConfigMutation: PropTypes.func.isRequired,
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired
  };

  state = {
    activeSegment: undefined,
    menuOpen: false,
    showUpdateTraceTray: false,
    showTraceInfoConfigEditor: false,
    showTraceState: false,
    compareLinksModalOpen: false,
    // Session-local: technical migration links are hidden for everyone until an
    // admin asks for them.
    showMigrationLinks: false
  };

  setDocTitle() {
    const { traceQuery: { traceById: { name } = {} } = {} } = this.props;
    if (name) document.title = `${name} - Inspector - Trace`;
  }

  getLinkActionConfig = (link, workflowActions) => {
    const actionConfig = workflowActions?.find(a => a.key === link?.actionKey);
    return actionConfig;
  };

  // A link the history does not show: an answer-style hidden action, or a
  // technical migration the admin toggle has not revealed. Migration is read from
  // `link.action` (the link's own config version) rather than the workflow's
  // current actions, so a link stays flagged even if the flag is later removed.
  isLinkHiddenFromHistory = (link, workflowActions) =>
    Boolean(this.getLinkActionConfig(link, workflowActions)?.hidden) ||
    (!this.state.showMigrationLinks && isMigrationLink(link));

  findUnprocessedCommentIndex = (comments, enrichedIndices, targetComment) => {
    for (let i = 0; i < comments.length; i++) {
      if (!enrichedIndices.has(i) && comments[i].comment === targetComment) {
        return i;
      }
    }
    return -1;
  };

  setInitialUrl = traceById => {
    const { history } = this.props;

    let headLink = traceById.head;

    // get hidden property from link's action config
    const headTraceActionIsHidden = this.isLinkHiddenFromHistory(
      traceById?.head,
      traceById?.workflow?.actions?.nodes
    );

    // if head trace action is hidden (e.g: answer, or a migration) we look for the
    // most recent link that IS shown and inject it as head instead of the 'real'
    // head — a migration run leaves its own link at the head of every trace it
    // touched, so this is the common case right after one.
    if (headTraceActionIsHidden) {
      const links = traceById?.links.nodes;

      headLink = links
        ?.filter(
          l =>
            !this.isLinkHiddenFromHistory(
              l,
              traceById?.workflow?.actions?.nodes
            )
        )
        .reduce(
          (highest, l) => (!highest || l.height > highest.height ? l : highest),
          undefined
        );
    }

    history.replace(
      Path.compile(ROUTE_INSPECT_TRACE_LINK)({
        id: traceById.rowId,
        linkid: headTraceActionIsHidden
          ? headLink?.linkHash
          : traceById.head.linkHash
      })
    );
  };

  componentDidMount() {
    this.setDocTitle();
  }

  scrollToTop() {
    const contentDiv = document.getElementById('segmentContent');
    if (contentDiv) {
      contentDiv.scrollTop = 0;
    }
  }

  componentDidUpdate(prevProps) {
    this.scrollToTop();
    if (prevProps.traceQuery !== this.props.traceQuery) this.setDocTitle();
    // updates segment view when actions are selected
    if (prevProps.match?.params?.linkid !== this.props.match?.params?.linkid) {
      const { traceById, loading } = this.props.traceQuery;
      if (!loading) this.setActiveSegment(traceById);
    }
  }

  handleTraceInfoConfigUpdate = async newTraceInfoConfigStr => {
    const {
      traceQuery: {
        traceById: { workflow }
      },
      updateTraceInfoConfigMutation
    } = this.props;

    const { rowId: workflowRowId, config: { rowId: workflowConfigId } = {} } =
      workflow;
    const newTraceInfoConfig = newTraceInfoConfigStr
      ? JSON.parse(newTraceInfoConfigStr)
      : null;

    const updateTraceInfoPromise = updateTraceInfoConfigMutation({
      variables: {
        workflowConfigId,
        newTraceInfoConfig
      },
      optimisticResponse: {
        updateWorkflowConfigByRowId: {
          workflow: {
            rowId: workflowRowId,
            config: {
              rowId: workflowConfigId,
              info: newTraceInfoConfig,
              __typename: TYPE_WORKFLOW_CONFIG
            },
            __typename: TYPE_WORKFLOW
          },
          __typename: TYPE_UPDATE_WORKFLOW_CONFIG_PAYLOAD
        }
      }
    });

    notify.promise(updateTraceInfoPromise, {
      loading: 'Updating trace info configuration...',
      success: 'The trace info config was correctly updated',
      error: data => data.message
    });

    await to(updateTraceInfoPromise);
  };

  toggleShowWorkflowConfigEditor = () =>
    this.setState(prevState => ({
      showTraceInfoConfigEditor: !prevState.showTraceInfoConfigEditor
    }));

  toggleShowStateEditor = () =>
    this.setState(prevState => ({
      showTraceState: !prevState.showTraceState
    }));

  toggleCompareLinksModal = () =>
    this.setState(prevState => ({
      compareLinksModalOpen: !prevState.compareLinksModalOpen
    }));

  toggleShowMigrationLinks = () =>
    this.setState(prevState => ({
      showMigrationLinks: !prevState.showMigrationLinks
    }));

  toggleMenu = () =>
    this.setState(prevState => ({ menuOpen: !prevState.menuOpen }));

  subscribeToTrace(props) {
    const { traceQuery, match } = props;
    this.unsubscribeFromTrace = traceQuery.subscribeToMore({
      document: subscriptions.trace,
      variables: { id: `trace:${match.params.id}` },
      updateQuery: (prev, data) => {
        const next = data?.subscriptionData?.data?.listen?.relatedNode;
        if (!next) return prev;
        // The Trace.links type policy (traceClient.ts) already dedupes by
        // linkHash and sorts by height DESC on every merge, so we just hand
        // the subscription payload through — no need to hand-roll a second
        // implementation here.
        return { traceById: { ...prev.traceById, ...next } };
      }
    });
  }

  UNSAFE_componentWillMount() {
    this.subscribeToTrace(this.props);
  }

  componentWillUnmount() {
    if (this.unsubscribeFromTrace) this.unsubscribeFromTrace();
  }

  shouldComponentUpdate(props) {
    const {
      traceQuery: { loading, traceById: trace, error }
    } = props;
    return !loading && (!!trace || !!error);
  }

  UNSAFE_componentWillReceiveProps = nextProps => {
    const {
      traceQuery: { loading, traceById: trace, error },
      errorContext: { handleError },
      match: { params }
    } = nextProps;
    if (loading) return;
    // This check avoids rerendering the component after the erroBoundary has been triggered
    if (!trace) {
      handleError('trace', params.id, ROUTE_WORKFLOW_DASHBOARD, error);
      return;
    }
    if (params?.linkid) {
      // If the linkid isn't yet in the paginated window (e.g. browser-back to
      // an older deep link that hasn't been paged in), setActiveSegment
      // tolerates .find returning undefined; pagination will surface it as
      // the user scrolls. Only the initial anchor resolve in the wrapper
      // treats "target height not found" as a 404.
      this.setActiveSegment(trace);
    } else this.setInitialUrl(trace);
  };

  getActiveSegment = (trace, height) => {
    const workflowActions = trace.workflow.actions.nodes;
    // filters links to remove hidden actions (e.g: answers) and unrevealed migrations
    const notHiddenLinks = trace.links.nodes.filter(l => {
      if (isMigrationLink(l) && !this.state.showMigrationLinks) {
        return false;
      }
      if (workflowActions.includes(l.actionKey)) {
        return workflowActions.some(a => a.key === l.actionKey && !a.hidden);
      }
      return true;
    });

    if (height) {
      const h = Number.parseInt(height, 10);
      const idx = notHiddenLinks.findIndex(l => l.height === h);
      if (idx === -1) {
        throw new Error(`Invalid trace height ${h}`);
      }
      return notHiddenLinks[idx];
    }

    return trace.head;
  };

  setActiveSegment = traceById => {
    const { links, head, workflow } = traceById;

    const headTraceActionIsHidden = this.isLinkHiddenFromHistory(
      head,
      workflow?.actions?.nodes
    );

    const segment = links.nodes.find(l =>
      this.props.match.params.linkid || headTraceActionIsHidden
        ? l.linkHash === this.props.match.params.linkid
        : l.linkHash === head.linkHash
    );

    // A hard-scroll re-anchor fires an offset-anchored query that REPLACES the
    // loaded window with a far region that no longer contains the selected
    // link. In that case `.find` returns undefined — but the selection hasn't
    // changed, so keep the previously resolved segment mounted (the ribbon
    // shows the new region) instead of blanking the whole inspector into the
    // spinner. Only overwrite when we actually resolved a segment, or when the
    // selection itself changed (a new linkid the window doesn't cover yet).
    const selectionChanged =
      this.state.activeSegment?.linkHash !== this.props.match.params.linkid;

    if (segment || selectionChanged) {
      this.setState({
        activeSegment: segment
      });
    }
  };

  getNextActions = ({ excludeHidden, excludeComment } = {}) => {
    const {
      traceQuery: {
        traceById: {
          workflow: { groups, actions },
          state: { nextActions }
        }
      }
    } = this.props;

    const nextActionsArray = getNextActionsArray({
      nextActions,
      groups: groups.nodes,
      actions: actions.nodes
    });

    return nextActionsArray?.reduce((result, actionsByGroups) => {
      const filteredActions = actionsByGroups.actions?.filter(action => {
        if (
          (excludeHidden && action.hidden) ||
          (excludeComment && action.key === 'comment')
        ) {
          return false;
        }
        return true;
      });

      if (filteredActions?.length > 0) {
        result.push({ ...actionsByGroups, actions: filteredActions });
      }
      return result;
    }, []);
  };

  toggleShowUpdateTraceTray = () => {
    const { showUpdateTraceTray } = this.state;
    this.setState({
      showUpdateTraceTray: !showUpdateTraceTray
    });
  };

  getTasks = () => {
    const {
      traceQuery: {
        traceById: {
          state: { tasks }
        }
      }
    } = this.props;
    return tasks;
  };

  getWorkflowId = () => {
    const {
      traceQuery: { traceById: trace }
    } = this.props;
    return trace.workflow.rowId;
  };

  getArchivedInfo = () => {
    const {
      traceQuery: { traceById: trace }
    } = this.props;
    const state = trace?.state;
    return {
      archived: Boolean(state?.archived),
      archivedAt: state?.archivedAt ?? null,
      manuallyArchived: Boolean(state?.manuallyArchived)
    };
  };

  renderHeader = () => {
    const {
      traceQuery: { loading: traceLoading, traceById: trace },
      user: { loading: userLoading, me }
    } = this.props;
    const loading = traceLoading || userLoading;
    const isPrivileged = hasElevatedRights(me);

    const { showTraceInfoConfigEditor, showTraceState } = this.state;

    let info;
    let state;
    let traceInfoConfigStr;
    let traceStateStr;
    if (!loading) {
      ({
        workflow: {
          config: { info }
        },
        state
      } = trace);
      traceInfoConfigStr = stringify(info, null, 2);
      traceStateStr = stringify(state, null, 2);
    }

    const archivedInfo = loading
      ? { archived: false, archivedAt: null, manuallyArchived: false }
      : this.getArchivedInfo();

    const configHeader = {
      loading,
      bottomLevel: {
        workflowPage: true,
        infoContext: {
          links: [
            {
              icon: loading ? null : 'Trace',
              label: loading ? null : trace.name,
              node:
                !loading && archivedInfo.archived ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Archive
                          className="text-muted-foreground"
                          data-cy="archived-trace-lock"
                          size={14}
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        {formatArchivedTooltip(archivedInfo)}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : null
            }
          ]
        },
        actions: {
          links: [
            ...(isPrivileged
              ? [
                  {
                    icon: <Wrench />,
                    label: 'Technical migration',
                    // The label no longer says show/hide, so the button carries
                    // whether the links are revealed.
                    className: this.state.showMigrationLinks
                      ? 'bg-foreground/5 text-foreground'
                      : undefined,
                    onClick: this.toggleShowMigrationLinks
                  }
                ]
              : []),
            {
              icon: <GitCompare />,
              label: 'Compare Links',
              onClick: this.toggleCompareLinksModal
            },
            ...(isPrivileged
              ? [
                  {
                    icon: <Settings />,
                    label: 'Trace Info configuration',
                    onClick: this.toggleShowWorkflowConfigEditor
                  },
                  {
                    icon: <Database />,
                    label: 'See Trace State',
                    onClick: this.toggleShowStateEditor
                  }
                ]
              : [])
          ]
        }
      }
    };

    if (trace?.workflow) {
      configHeader.bottomLevel.infoContext.links.unshift({
        icon: 'TableColumns',
        label: trace.workflow?.name ?? 'Workflow',
        path: Path.compile(ROUTE_WORKFLOW_OVERVIEW)({
          id: trace.workflow?.rowId
        })
      });
    }

    return (
      <>
        <Navbar config={configHeader} />
        {isPrivileged && showTraceInfoConfigEditor && (
          <JsonEditor
            jsonString={traceInfoConfigStr}
            onClose={this.toggleShowWorkflowConfigEditor}
            onSubmit={this.handleTraceInfoConfigUpdate}
            title="Trace Info configuration"
          />
        )}
        {isPrivileged && showTraceState && (
          <JsonEditor
            jsonString={traceStateStr}
            onClose={this.toggleShowStateEditor}
            title="Trace State"
          />
        )}
        {!loading && (
          <CompareLinksDiffModal
            currentLinkHash={this.state.activeSegment?.linkHash}
            links={trace.links?.nodes || []}
            onOpenChange={this.toggleCompareLinksModal}
            open={this.state.compareLinksModalOpen}
          />
        )}
      </>
    );
  };

  handleLocalStorage = ({ index, isCollapsed }) => {
    const {
      traceQuery: { traceById }
    } = this.props;
    return manageLocalStorage(traceById, { index, isCollapsed });
  };

  goToActionLink = (link, traceIds) => {
    const { groupKey, actionKey } = link;

    const baseUrl = Path.compile(ROUTE_NEW_LINK)({
      wfid: this.getWorkflowId()
    });

    let traceIdsString = '';
    if (traceIds && traceIds.length > 0) {
      traceIdsString = `&traceIds=${traceIds.join(',')}`;
    }

    return this.props.history.push(
      `${baseUrl}?groupKey=${groupKey}&actionKey=${actionKey}${traceIdsString}`,
      {
        from: this.props.history.location
      }
    );
  };

  renderNextActionButton = () => {
    const {
      traceQuery: {
        variables: { traceId }
      }
    } = this.props;

    // Archived traces are frozen: no live action CTA. In the exact slot the
    // next-action button occupies (the ribbon pulldown), we render a disabled
    // "Archived · no actions" button instead — an archive icon + muted label,
    // with the who/when detail in a hover tooltip. We use aria-disabled (not the
    // native disabled attribute) so the button stays focusable and the tooltip
    // still fires for hover and keyboard users.
    const archivedInfo = this.getArchivedInfo();
    if (archivedInfo.archived) {
      return (
        <ButtonInspectorWrapper>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  aria-disabled
                  className="cursor-not-allowed gap-1.5 bg-card text-muted-foreground opacity-100"
                  data-cy="archived-action-button"
                  onClick={event => event.preventDefault()}
                  variant="outline"
                >
                  <Archive size={14} />
                  Archived · no actions
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {formatArchivedTooltip(archivedInfo)}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </ButtonInspectorWrapper>
      );
    }

    const nextActionsNotHidden = this.getNextActions({
      excludeHidden: true
    });

    // No actions = nothing to do
    if (nextActionsNotHidden.length === 0) {
      return (
        <ButtonInspectorWrapper>
          <Button
            className="bg-card disabled:opacity-100"
            disabled
            variant="outline"
          >
            Nothing to do
          </Button>
        </ButtonInspectorWrapper>
      );
    }
    // Just one group can act
    if (nextActionsNotHidden.length === 1) {
      // Only one action is possible
      if (nextActionsNotHidden[0].actions.length === 1) {
        const action = nextActionsNotHidden[0].actions[0];
        const { group } = nextActionsNotHidden[0];
        const isComment = action.key === 'comment';
        return (
          <ButtonInspectorWrapper>
            <Button
              className={isComment ? 'bg-card' : ''}
              data-cy="next-action-button"
              onClick={() => {
                this.goToActionLink(
                  {
                    groupKey: group.label,
                    actionKey: action.key
                  },
                  [traceId]
                );
              }}
              variant={isComment ? 'outline' : 'default'}
            >
              {action.title}
            </Button>
          </ButtonInspectorWrapper>
        );
      }

      // Get the possible "comment" next action
      const commentAction = nextActionsNotHidden[0].actions.find(
        action => action.key === 'comment'
      );

      // 2 possible actions, on of them is "comment"
      if (nextActionsNotHidden[0].actions.length === 2 && !!commentAction) {
        const primaryAction = nextActionsNotHidden[0].actions.find(
          action => action.key !== 'comment'
        );
        const { group } = nextActionsNotHidden[0];
        return (
          <ButtonInspectorWrapper>
            <Button
              className="w-full"
              data-cy="next-action-button"
              onClick={() => {
                this.goToActionLink(
                  {
                    groupKey: group.label,
                    actionKey: primaryAction.key
                  },
                  [traceId]
                );
              }}
            >
              {primaryAction.title}
            </Button>
            <Button
              className="w-full bg-card"
              onClick={() => {
                this.goToActionLink(
                  {
                    groupKey: group.label,
                    actionKey: commentAction.key
                  },
                  [traceId]
                );
              }}
              variant="outline"
            >
              {commentAction.title}
            </Button>
          </ButtonInspectorWrapper>
        );
      }
    }

    // Multiple groups we display the standard "Next Actions" button that opens a tray
    return (
      <ButtonInspectorWrapper>
        <Button
          data-cy="next-action-button"
          onClick={this.toggleShowUpdateTraceTray}
        >
          Next action
        </Button>
      </ButtonInspectorWrapper>
    );
  };

  renderInspector = () => {
    const {
      traceQuery: { traceById: trace }
    } = this.props;
    const { activeSegment, showUpdateTraceTray } = this.state;
    const nextActionsNotHidden = this.getNextActions({ excludeHidden: true });
    const tasks = this.getTasks();

    const { rowId: traceId } = trace;

    let newTracesTrayMessage =
      'The following actions are available for this trace.';
    if (showUpdateTraceTray && !nextActionsNotHidden.length) {
      newTracesTrayMessage = 'No actions are available for this trace.';
    }

    return (
      !this.props.traceQuery.loading && (
        <>
          <SegmentList
            activeSegment={activeSegment}
            links={trace.links.nodes}
            loading={this.props.tracePagination?.loading ?? false}
            onLoadRange={this.props.tracePagination?.loadRange}
            pulldown={this.renderNextActionButton()}
            showMigrations={this.state.showMigrationLinks}
            totalCount={trace.links.totalCount}
            traceId={trace.rowId}
          />
          <Vaul
            description={newTracesTrayMessage}
            open={showUpdateTraceTray}
            setOpen={this.toggleShowUpdateTraceTray}
            title="Next action"
          >
            <ActionsList
              message={newTracesTrayMessage}
              nextActions={nextActionsNotHidden}
              tasks={tasks}
              toggleTray={this.toggleShowUpdateTraceTray}
              traceIds={[traceId]}
              workflowId={this.getWorkflowId()}
            />
          </Vaul>
        </>
      )
    );
  };

  renderSegment = () => {
    const {
      traceQuery: { refetch, traceById: trace }
    } = this.props;
    const { activeSegment } = this.state;
    const nextActions = this.getNextActions();
    const { rowId: traceId, workflow } = trace;

    // populating groups with a hidden nextAction (e.g: answers)
    const groups = nextActions
      .filter(a => a.actions.find(b => b.hidden))
      .map(n => n.group);

    const answers = trace.links.nodes
      .filter(l => l.data?.commentLinkHash === activeSegment.linkHash)
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));

    // build the workflow context passed to the form reader
    const workflowContext = buildWorkflowContext(workflow);

    return (
      <Switch>
        <Route exact path={ROUTE_INSPECT_TRACE_LINK}>
          <SideContainer>
            <div className="overflow-hidden rounded-md border border-border bg-card p-4">
              <SegmentInfo
                activeSegment={activeSegment}
                answers={answers}
                groups={groups}
                links={trace.links.nodes}
                nextActions={nextActions}
                refetchTraceQuery={() => refetch()}
                traceHead={trace.head}
                traceId={traceId}
                workflowContext={workflowContext}
              />
            </div>
          </SideContainer>
        </Route>
      </Switch>
    );
  };

  renderTraceInfo = () => {
    const {
      traceQuery: { traceById: trace },
      match
    } = this.props;

    let { rowId: traceId, state, workflow, links } = trace;
    const commentAnswers = links.nodes.filter(
      l => l.actionKey === 'answerComment'
    );

    const {
      config: { info },
      rowId: workflowRowId,
      actions: { nodes: workflowActions }
    } = workflow;
    const linksList = links.nodes;

    // build the workflow context passed to the form reader
    const workflowContext = buildWorkflowContext(workflow);

    const wfUserDisplayConfig = getUserInfoDisplayConfig(workflowRowId);

    const localStorageContext = {
      userInfoConfig:
        wfUserDisplayConfig?.[traceId] ||
        sectionsLocalStorage(workflow?.config?.info?.view?.sections || []),
      setLocalStorage: this.handleLocalStorage
    };

    const traceInspectorContext = {
      links: linksList,
      traceId: trace.rowId,
      currentHash: match.params.linkid
    };

    const enrichComments = () => {
      /**
       * @type {Comment[]}
       */
      const enrichedComments = [...(state.data.comments ?? [])];
      /**
       * Set to track which comment indices have already been enriched.
       * This prevents duplicate processing when multiple comments have the same text.
       * @type {Set<number>}
       */
      const enrichedIndices = new Set();

      for (const link of linksList) {
        const matchingAction = this.getLinkActionConfig(link, workflowActions);
        const answerableKeys = matchingAction?.answerableComments;

        if (!answerableKeys || answerableKeys.length === 0) continue;

        const foundEntry = Object.entries(link.data || {}).find(([key]) =>
          answerableKeys.includes(key)
        );
        const linkComment = foundEntry?.[1];

        if (!linkComment) continue;

        const answersForLink = commentAnswers?.filter(
          answer => answer.data?.commentLinkHash === link.linkHash
        );

        const newComment = {
          // ? We add this property to identify the comment as added by the front
          isEnrichedByFront: true,
          action: link.action.title,
          answers: answersForLink,
          comment: linkComment,
          date: link.createdAt,
          files: link.data.files,
          group: link.group.name,
          groupId: link.group.rowId,
          groupLabel: link.group.label,
          height: link.height,
          id: link.id,
          linkHash: link.linkHash,
          user: {
            name: link.createdBy?.name || 'Unknown user',
            avatar: link.createdBy?.avatar,
            accountId: link.createdBy?.id
          }
        };

        const existingIndex = this.findUnprocessedCommentIndex(
          enrichedComments,
          enrichedIndices,
          linkComment
        );

        if (existingIndex > -1) {
          enrichedComments[existingIndex] = newComment;
          enrichedIndices.add(existingIndex);
        }
      }
      return enrichedComments;
    };

    if (state.data.comments) {
      state = {
        ...state,
        data: {
          ...state.data,
          comments: enrichComments()
        }
      };
    }

    return (
      <SideContainer>
        {info && (
          <WorkflowContext.Provider value={workflowContext}>
            <LocalStorageContext.Provider value={localStorageContext}>
              <TraceInspectorContext.Provider value={traceInspectorContext}>
                <Widget data={state} widget={info} />
              </TraceInspectorContext.Provider>
            </LocalStorageContext.Provider>
          </WorkflowContext.Provider>
        )}
      </SideContainer>
    );
  };

  render = () => {
    const {
      traceQuery: { loading, error, traceById: trace }
    } = this.props;

    const { activeSegment } = this.state;

    /**
     * If the trace id doesn't exist,
     * we return null and let errorBoundary render the error page
     */
    if (error || !(trace || loading)) {
      return null;
    }

    return (
      <>
        <div id={TOOLTIP_PORTAL} />
        <div
          className="fixed top-0 right-0 bottom-0 z-10 flex w-auto flex-row-reverse flex-nowrap data-[is-left=true]:left-0 data-[is-left=true]:flex-row data-[is-left=true]:[right:unset]"
          id={TRAY_PORTAL_RIGHT}
        />
        {this.renderHeader()}
        {loading || !activeSegment ? (
          <TraceIconSpinner />
        ) : (
          <div className="flex h-[calc(100vh-var(--spacing-navbar))] w-screen flex-col flex-nowrap overflow-hidden bg-background">
            {this.renderInspector()}
            <div
              className="scrollbar-stable scrollbar-thin grid grid-cols-1 gap-6 overflow-y-auto overflow-x-hidden p-6 pb-0 lg:grid-cols-2"
              id="segmentContent"
            >
              {this.renderSegment()}
              {this.renderTraceInfo()}
              <Footer className="relative" />
            </div>
          </div>
        )}
      </>
    );
  };
}

const queries = {
  traceQuery: gql`
    query traceQuery(
      $traceId: UUID!
      $first: Int = 20
      $after: Cursor
      $last: Int
      $before: Cursor
      $offset: Int
    ) {
      traceById(id: $traceId) {
        ...TraceInspectorFragment
        links(
          first: $first
          after: $after
          last: $last
          before: $before
          offset: $offset
        ) {
          totalCount
          pageInfo {
            startCursor
            endCursor
            hasNextPage
            hasPreviousPage
          }
          nodes {
            ...TraceLinkFragment
          }
        }
      }
    }
    ${fragments.trace}
    ${fragments.traceLink}
  `
};

const mutations = {
  updateTraceInfoConfig: gql`
    mutation updateTraceInfoConfigMutation(
      $workflowConfigId: BigInt!
      $newTraceInfoConfig: JSON
    ) {
      updateWorkflowConfigByRowId(
        input: {
          rowId: $workflowConfigId
          patch: { info: $newTraceInfoConfig }
        }
      ) {
        workflow {
          rowId
          config {
            rowId
            info
          }
        }
      }
    }
  `
};

const subscriptions = {
  trace: gql`
    subscription listenTrace($id: String!) {
      listen(topic: $id) {
        relatedNodeId
        relatedNode {
          id
          ... on Trace {
            ...TraceInspectorFragment
            links(first: 1) {
              nodes {
                ...TraceLinkFragment
              }
            }
          }
        }
      }
    }
    ${fragments.trace}
    ${fragments.traceLink}
  `
};

const TraceInspectorWithQuery = compose(
  withUser,
  withRouter,
  graphql(FILTERS_QUERY, {
    name: 'filtersQuery'
  }),
  graphql(mutations.updateTraceInfoConfig, {
    name: 'updateTraceInfoConfigMutation'
  }),
  graphql(queries.traceQuery, {
    name: 'traceQuery',
    // The initial window is driven by `anchorOffset`: 0 when opening on the
    // head, or the offset that centers a deep-linked (older) link. Anchoring
    // on `offset` — instead of loading the head then paging older until the
    // target shows up — is what avoids the bulk "90→70→50→30" load. Because
    // the cache merges all unfiltered pages into one contiguous entry, we
    // must NOT also load the head window, or the merged array would have a
    // gap; the anchored window is the sole initial fetch.
    options: ({ match, anchorOffset }) => ({
      variables: {
        traceId: match.params.id,
        first: TRACE_LINKS_FIRST,
        after: null,
        offset: anchorOffset ?? 0
      },
      fetchPolicy: 'cache-and-network'
    })
  }),
  withError
)(withTraceLinksPagination(TraceInspectorImpl));

function withTraceLinksPagination(Wrapped) {
  return function TraceInspectorWithPagination(props) {
    const trace = props.traceQuery?.traceById;
    const pagination = useTraceLinksPagination(
      props.traceQuery?.fetchMore,
      trace?.rowId ?? ''
    );
    return <Wrapped {...props} tracePagination={pagination} />;
  };
}

export function TraceInspector(props) {
  const { match } = props;
  const { offset, resolving } = useTraceLinkAnchor(
    match?.params?.id,
    match?.params?.linkid
  );

  // Gate the main query until the anchor resolves, so the very first links
  // fetch is already centered on the deep link (no head-then-jump flicker).
  if (resolving) {
    return <TraceIconSpinner />;
  }

  return <TraceInspectorWithQuery {...props} anchorOffset={offset} />;
}

const SideContainer = ({ children }) => (
  <div className="w-full">{children}</div>
);

const ButtonInspectorWrapper = ({ children }) => (
  <div className="before:-z-10 relative pt-[30px] pr-[100px] pl-[50px] before:absolute before:top-[80px] before:left-0 before:h-[1px] before:w-[90px] before:bg-border before:content-['']">
    <div className="flex flex-col items-center gap-2 py-[35px] pb-[17px]">
      {children}
    </div>
  </div>
);

// Human-readable "who + when" for the archived tooltips. No archived-by
// identity is stored, so we distinguish a manual archive (a user acted via the
// UI) from an automatic one (the archive cron) and append the archived date
// when known.
export const formatArchivedTooltip = ({ archivedAt, manuallyArchived }) => {
  const who = manuallyArchived
    ? 'This trace was archived manually'
    : 'This trace was auto-archived by the system';
  if (!archivedAt) {
    return `${who}.`;
  }
  const date = new Date(archivedAt);
  if (Number.isNaN(date.getTime())) {
    return `${who}.`;
  }
  // Follow the browser's locale (navigator.language) in a numeric
  // day/month/year format so a French browser reads "20/07/2026" instead of
  // the runtime-default US "7/20/2026". A bare toLocaleDateString() ignores the
  // browser and uses the runtime default locale.
  const formatted = date.toLocaleDateString(navigator.language, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  return `${who} on ${formatted}.`;
};
